import { useState, useEffect } from 'react'
import { PortalLayout } from '../components/PortalLayout'
import { api } from '../api'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts'

const TABS = ['invoices', 'aged_debt', 'revenue', 'settings'] as const
type Tab = typeof TABS[number]

const STATUS_BADGE_COLORS: Record<string, string> = {
  draft: 'bg-slate-200 text-slate-800',
  sent: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-800',
  disputed: 'bg-amber-100 text-amber-800',
  credited: 'bg-purple-100 text-purple-800',
}

interface Invoice {
  id: number
  invoice_number: string
  customer_id: number
  customer_name: string
  issue_date: string
  due_date: string
  total: number
  status: string
  lines?: InvoiceLine[]
  payments?: Payment[]
  balance_due?: number
  sent_at?: string
  sent_to_email?: string
  created_at?: string
  subtotal?: number
  vat_rate?: number
  vat_amount?: number
  currency?: string
  payment_terms_days?: number
  po_number?: string
  notes?: string
  total_paid?: number
}

interface InvoiceLine {
  id: number
  job_id?: number
  description: string
  quantity: number
  unit_price: number
  line_total: number
  vat_rate: number
  vat_amount: number
  sort_order: number
}

interface Payment {
  id: number
  amount: number
  payment_date: string
  payment_method: string
  reference?: string
  notes?: string
  created_at?: string
}

interface AgedDebtRow {
  customer_id?: number
  customer_name: string
  current: number
  '30': number
  '60': number
  '90+': number
  total: number
}

interface RevenueData {
  [key: string]: number | string
}

interface TopCustomer {
  name: string
  revenue: number
}

interface InvoiceSettings {
  invoice_prefix?: string
  next_invoice_number?: number
  default_payment_terms_days?: number
  default_vat_rate?: number
  bank_name: string
  sort_code: string
  account_number: string
  company_registration: string
  vat_number: string
  invoice_footer_text: string
}

interface Job {
  id: number
  job_number: string
  customer_id: number
  customer_name: string
  status: string
  completed_date?: string
}

export function AccountsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('invoices')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Invoices state
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [statusFilter, setStatusFilter] = useState('all')
  const [expandedInvoiceId, setExpandedInvoiceId] = useState<number | null>(null)
  const [showGenerateModal, setShowGenerateModal] = useState(false)
  const [uninvoicedJobs, setUninvoicedJobs] = useState<Record<string, Job[]>>({})
  const [selectedJobs, setSelectedJobs] = useState<Set<number>>(new Set())

  // Aged Debt state
  const [agedDebtData, setAgedDebtData] = useState<AgedDebtRow[]>([])

  // Revenue state
  const [revenueData, setRevenueData] = useState<RevenueData[]>([])
  const [topCustomers, setTopCustomers] = useState<TopCustomer[]>([])

  // Settings state
  const [settings, setSettings] = useState<InvoiceSettings>({
    invoice_prefix: 'INV',
    default_payment_terms_days: 30,
    default_vat_rate: 20,
    bank_name: '',
    sort_code: '',
    account_number: '',
    company_registration: '',
    vat_number: '',
    invoice_footer_text: '',
  })
  const [settingsLoading, setSettingsLoading] = useState(false)

  // Load invoices
  const loadInvoices = async () => {
    setLoading(true)
    setError(null)
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {}
      const response = await api.getInvoices(params)
      // Backend returns { total: number, page: number, page_size: number, invoices: [...] }
      setInvoices(response.invoices || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoices')
    } finally {
      setLoading(false)
    }
  }

  // Load invoice details
  const loadInvoiceDetail = async (invoiceId: number) => {
    try {
      const detail = await api.getInvoice(invoiceId)
      setInvoices(prev =>
        prev.map(inv => (inv.id === invoiceId ? { ...inv, ...detail } : inv))
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoice details')
    }
  }

  // Load aged debt
  const loadAgedDebt = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.getAgedDebt()
      // Backend returns { customers: [...], totals: {...} }
      const customers = response.customers || []
      setAgedDebtData(customers)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load aged debt')
    } finally {
      setLoading(false)
    }
  }

  // Load revenue
  const loadRevenue = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await api.getRevenueSummary(12)
      // Backend returns { by_month: {...}, by_customer: {...}, total: number }
      const chartData: RevenueData[] = Object.entries(data.by_month || {}).map(([month, revenue]) => ({
        month,
        revenue: revenue as number,
      }))
      const topCustList: TopCustomer[] = Object.entries(data.by_customer || {})
        .sort((a, b) => (b[1] as number) - (a[1] as number))
        .map(([name, revenue]) => ({
          name,
          revenue: revenue as number,
        }))
      setRevenueData(chartData)
      setTopCustomers(topCustList)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load revenue data')
    } finally {
      setLoading(false)
    }
  }

  // Load settings
  const loadSettings = async () => {
    setSettingsLoading(true)
    try {
      const data = await api.getInvoiceSettings()
      setSettings(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings')
    } finally {
      setSettingsLoading(false)
    }
  }

  // Load uninvoiced jobs
  const loadUninvoicedJobs = async () => {
    try {
      const jobs = await api.getJobs({ status: 'completed' })
      const grouped: Record<string, Job[]> = {}
      jobs.forEach((job: any) => {
        const customerName = job.customer_name || 'Unknown'
        if (!grouped[customerName]) {
          grouped[customerName] = []
        }
        grouped[customerName].push(job)
      })
      setUninvoicedJobs(grouped)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load jobs')
    }
  }

  // Tab change effects
  useEffect(() => {
    switch (activeTab) {
      case 'invoices':
        loadInvoices()
        break
      case 'aged_debt':
        loadAgedDebt()
        break
      case 'revenue':
        loadRevenue()
        break
      case 'settings':
        loadSettings()
        break
    }
  }, [activeTab, statusFilter])

  // Handlers
  const handleExpandInvoice = (invoiceId: number) => {
    if (expandedInvoiceId === invoiceId) {
      setExpandedInvoiceId(null)
    } else {
      setExpandedInvoiceId(invoiceId)
      loadInvoiceDetail(invoiceId)
    }
  }

  const handleGenerateInvoices = async () => {
    if (selectedJobs.size === 0) {
      setError('Please select at least one job')
      return
    }

    setLoading(true)
    try {
      const jobIds = Array.from(selectedJobs)
      await api.generateInvoiceFromJobs({ job_ids: jobIds })
      setShowGenerateModal(false)
      setSelectedJobs(new Set())
      loadInvoices()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate invoices')
    } finally {
      setLoading(false)
    }
  }

  const handleSendInvoice = async (invoiceId: number) => {
    try {
      await api.sendInvoice(invoiceId)
      loadInvoices()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invoice')
    }
  }

  const handleRecordPayment = async (invoiceId: number, amount: number, method: string) => {
    try {
      await api.addPayment(invoiceId, { amount, method, date: new Date().toISOString() })
      loadInvoiceDetail(invoiceId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record payment')
    }
  }

  const handleSaveSettings = async () => {
    setSettingsLoading(true)
    try {
      await api.updateInvoiceSettings(settings)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings')
    } finally {
      setSettingsLoading(false)
    }
  }

  const toggleJobSelection = (jobId: number) => {
    const newSelected = new Set(selectedJobs)
    if (newSelected.has(jobId)) {
      newSelected.delete(jobId)
    } else {
      newSelected.add(jobId)
    }
    setSelectedJobs(newSelected)
  }

  const handlePaymentClick = (invoiceId: number) => {
    // Would open a modal in real implementation
    const amount = prompt('Enter payment amount:')
    if (amount) {
      handleRecordPayment(invoiceId, parseFloat(amount), 'manual')
    }
  }

  return (
    <PortalLayout currentPage="accounts">
      <div className="space-y-6">
        {/* Header */}
        <div className="border-b border-slate-200">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">ACCOUNTS & INVOICING</h1>

          {/* Tabs */}
          <div className="flex gap-8 border-t border-slate-200">
            {TABS.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-1 py-4 text-xs font-semibold uppercase tracking-wide border-b-2 transition ${
                  activeTab === tab
                    ? 'border-slate-900 text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab === 'aged_debt' ? 'Aged Debt' : tab.charAt(0).toUpperCase() + tab.slice(1).replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-4">
            <p className="text-sm text-red-800">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-xs text-red-600 hover:text-red-800 mt-2"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Tab Content */}
        <div>
          {/* INVOICES TAB */}
          {activeTab === 'invoices' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex gap-2 items-center">
                  <label className="text-xs font-semibold text-slate-700 uppercase">Filter:</label>
                  <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    className="text-xs border border-slate-300 rounded px-3 py-2 bg-white text-slate-900"
                  >
                    <option value="all">All Statuses</option>
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                  </select>
                </div>
                <button
                  onClick={() => {
                    loadUninvoicedJobs()
                    setShowGenerateModal(true)
                  }}
                  className="px-4 py-2 bg-slate-900 text-white text-xs font-semibold uppercase rounded hover:bg-slate-800"
                >
                  Generate from Jobs
                </button>
              </div>

              {loading && !invoices.length ? (
                <div className="text-center py-8 text-slate-500">Loading invoices...</div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-8 text-slate-500">No invoices found</div>
              ) : (
                <div className="border border-slate-200 rounded overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 uppercase">Invoice #</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 uppercase">Customer</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 uppercase">Issue Date</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 uppercase">Due Date</th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-700 uppercase">Total</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map(invoice => (
                        <tr key={invoice.id}>
                          <td className="px-4 py-3 border-t border-slate-200">
                            <button
                              onClick={() => handleExpandInvoice(invoice.id)}
                              className="text-blue-600 hover:text-blue-800 font-semibold"
                            >
                              {invoice.invoice_number}
                            </button>
                          </td>
                          <td className="px-4 py-3 border-t border-slate-200 text-slate-900">{invoice.customer_name}</td>
                          <td className="px-4 py-3 border-t border-slate-200 text-slate-600">{new Date(invoice.issue_date).toLocaleDateString()}</td>
                          <td className="px-4 py-3 border-t border-slate-200 text-slate-600">{new Date(invoice.due_date).toLocaleDateString()}</td>
                          <td className="px-4 py-3 border-t border-slate-200 text-right text-slate-900 font-semibold">£{invoice.total.toFixed(2)}</td>
                          <td className="px-4 py-3 border-t border-slate-200">
                            <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${STATUS_BADGE_COLORS[invoice.status] || 'bg-slate-100 text-slate-800'}`}>
                              {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Expanded invoice detail */}
              {expandedInvoiceId && invoices.find(i => i.id === expandedInvoiceId) && (
                <div className="border border-slate-200 rounded p-4 bg-slate-50">
                  {(() => {
                    const invoice = invoices.find(i => i.id === expandedInvoiceId)!
                    return (
                      <div className="space-y-4">
                        <h3 className="font-semibold text-slate-900 uppercase text-sm">Invoice Details</h3>

                        {invoice.lines && invoice.lines.length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-slate-700 uppercase mb-2">Line Items</h4>
                            <table className="w-full text-xs border-collapse">
                              <thead>
                                <tr className="border-b border-slate-300">
                                  <th className="text-left py-2 px-2 text-slate-700">Description</th>
                                  <th className="text-right py-2 px-2 text-slate-700">Qty</th>
                                  <th className="text-right py-2 px-2 text-slate-700">Rate</th>
                                  <th className="text-right py-2 px-2 text-slate-700">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {invoice.lines.map(line => (
                                  <tr key={line.id} className="border-b border-slate-200">
                                    <td className="py-2 px-2 text-slate-900">{line.description}</td>
                                    <td className="text-right py-2 px-2 text-slate-600">{line.quantity}</td>
                                    <td className="text-right py-2 px-2 text-slate-600">£{line.unit_price.toFixed(2)}</td>
                                    <td className="text-right py-2 px-2 text-slate-900 font-semibold">£{line.line_total.toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {invoice.payments && invoice.payments.length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-slate-700 uppercase mb-2">Payments</h4>
                            <table className="w-full text-xs border-collapse">
                              <thead>
                                <tr className="border-b border-slate-300">
                                  <th className="text-left py-2 px-2 text-slate-700">Date</th>
                                  <th className="text-left py-2 px-2 text-slate-700">Method</th>
                                  <th className="text-right py-2 px-2 text-slate-700">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {invoice.payments.map(payment => (
                                  <tr key={payment.id} className="border-b border-slate-200">
                                    <td className="py-2 px-2 text-slate-600">{new Date(payment.payment_date).toLocaleDateString()}</td>
                                    <td className="py-2 px-2 text-slate-600">{payment.payment_method}</td>
                                    <td className="text-right py-2 px-2 text-slate-900 font-semibold">£{payment.amount.toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {invoice.balance_due !== undefined && (
                          <div className="border-t border-slate-300 pt-2 flex justify-between">
                            <span className="text-xs font-semibold text-slate-700 uppercase">Balance Due:</span>
                            <span className="text-sm font-bold text-slate-900">£{invoice.balance_due.toFixed(2)}</span>
                          </div>
                        )}

                        <div className="flex gap-2 pt-2">
                          {invoice.status !== 'sent' && invoice.status !== 'paid' && (
                            <button
                              onClick={() => handleSendInvoice(invoice.id)}
                              className="px-3 py-2 bg-blue-600 text-white text-xs font-semibold uppercase rounded hover:bg-blue-700"
                            >
                              Send
                            </button>
                          )}
                          {invoice.status !== 'paid' && (
                            <button
                              onClick={() => handlePaymentClick(invoice.id)}
                              className="px-3 py-2 bg-green-600 text-white text-xs font-semibold uppercase rounded hover:bg-green-700"
                            >
                              Record Payment
                            </button>
                          )}
                          <button
                            onClick={() => setExpandedInvoiceId(null)}
                            className="px-3 py-2 bg-slate-300 text-slate-900 text-xs font-semibold uppercase rounded hover:bg-slate-400"
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}
            </div>
          )}

          {/* GENERATE MODAL */}
          {showGenerateModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
                <div className="p-6 border-b border-slate-200">
                  <h2 className="text-lg font-bold text-slate-900 uppercase">Generate Invoices from Jobs</h2>
                </div>
                <div className="p-6 space-y-4">
                  {Object.entries(uninvoicedJobs).length === 0 ? (
                    <p className="text-sm text-slate-600">No uninvoiced completed jobs available</p>
                  ) : (
                    Object.entries(uninvoicedJobs).map(([customerName, jobs]) => (
                      <div key={customerName} className="border border-slate-200 rounded p-3">
                        <h3 className="font-semibold text-slate-900 text-sm mb-2">{customerName}</h3>
                        <div className="space-y-2">
                          {jobs.map(job => (
                            <label key={job.id} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedJobs.has(Number(job.id))}
                                onChange={() => toggleJobSelection(Number(job.id))}
                                className="w-4 h-4"
                              />
                              <span className="text-xs text-slate-700">{job.job_number}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-6 border-t border-slate-200 flex gap-2 justify-end">
                  <button
                    onClick={() => setShowGenerateModal(false)}
                    className="px-4 py-2 border border-slate-300 text-slate-900 text-xs font-semibold uppercase rounded hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGenerateInvoices}
                    disabled={selectedJobs.size === 0 || loading}
                    className="px-4 py-2 bg-slate-900 text-white text-xs font-semibold uppercase rounded hover:bg-slate-800 disabled:opacity-50"
                  >
                    Generate ({selectedJobs.size})
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* AGED DEBT TAB */}
          {activeTab === 'aged_debt' && (
            <div>
              {loading && !agedDebtData.length ? (
                <div className="text-center py-8 text-slate-500">Loading aged debt data...</div>
              ) : agedDebtData.length === 0 ? (
                <div className="text-center py-8 text-slate-500">No aged debt data available</div>
              ) : (
                <div className="border border-slate-200 rounded overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 uppercase">Customer</th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-700 uppercase">Current</th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-700 uppercase">30 Days</th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-700 uppercase">60 Days</th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-700 uppercase">90+ Days</th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-700 uppercase">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agedDebtData.map((row, idx) => (
                        <tr key={idx} className={idx === agedDebtData.length - 1 ? 'bg-slate-100 font-semibold' : ''}>
                          <td className="px-4 py-3 border-t border-slate-200 text-slate-900">{row.customer_name}</td>
                          <td className="px-4 py-3 border-t border-slate-200 text-right text-slate-600">£{row.current.toFixed(2)}</td>
                          <td className="px-4 py-3 border-t border-slate-200 text-right text-slate-600">£{row['30'].toFixed(2)}</td>
                          <td className="px-4 py-3 border-t border-slate-200 text-right text-slate-600">£{row['60'].toFixed(2)}</td>
                          <td className={`px-4 py-3 border-t border-slate-200 text-right ${row['90+'] > 0 ? 'text-red-700 font-semibold' : 'text-slate-600'}`}>
                            £{row['90+'].toFixed(2)}
                          </td>
                          <td className="px-4 py-3 border-t border-slate-200 text-right text-slate-900 font-semibold">£{row.total.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* REVENUE TAB */}
          {activeTab === 'revenue' && (
            <div className="space-y-6">
              {loading && !revenueData.length ? (
                <div className="text-center py-8 text-slate-500">Loading revenue data...</div>
              ) : (
                <>
                  {revenueData.length > 0 && (
                    <div className="border border-slate-200 rounded p-6 bg-white">
                      <h3 className="text-sm font-semibold text-slate-900 uppercase mb-4">Revenue by Month</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={revenueData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip formatter={(value: any) => `£${Number(value).toFixed(2)}`} />
                          <Bar dataKey="revenue" fill="#0f172a" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {topCustomers.length > 0 && (
                    <div className="border border-slate-200 rounded p-6 bg-white">
                      <h3 className="text-sm font-semibold text-slate-900 uppercase mb-4">Top Customers</h3>
                      <table className="w-full text-xs">
                        <thead className="border-b border-slate-200">
                          <tr>
                            <th className="text-left py-3 font-semibold text-slate-700 uppercase">Customer</th>
                            <th className="text-right py-3 font-semibold text-slate-700 uppercase">Revenue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topCustomers.map((customer, idx) => (
                            <tr key={idx} className="border-t border-slate-200">
                              <td className="py-3 text-slate-900">{customer.name}</td>
                              <td className="text-right py-3 text-slate-900 font-semibold">£{customer.revenue.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {revenueData.length === 0 && topCustomers.length === 0 && (
                    <div className="text-center py-8 text-slate-500">No revenue data available</div>
                  )}
                </>
              )}
            </div>
          )}

          {/* SETTINGS TAB */}
          {activeTab === 'settings' && (
            <div className="max-w-2xl">
              {settingsLoading ? (
                <div className="text-center py-8 text-slate-500">Loading settings...</div>
              ) : (
                <div className="border border-slate-200 rounded p-6 bg-white space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 uppercase mb-2">Invoice Prefix</label>
                      <input
                        type="text"
                        value={settings.invoice_prefix || ''}
                        onChange={e => setSettings({ ...settings, invoice_prefix: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded text-sm text-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 uppercase mb-2">Payment Terms (Days)</label>
                      <input
                        type="number"
                        value={settings.default_payment_terms_days || 30}
                        onChange={e => setSettings({ ...settings, default_payment_terms_days: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-slate-300 rounded text-sm text-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 uppercase mb-2">VAT Rate (%)</label>
                      <input
                        type="number"
                        value={settings.default_vat_rate || 20}
                        onChange={e => setSettings({ ...settings, default_vat_rate: parseFloat(e.target.value) })}
                        step="0.01"
                        className="w-full px-3 py-2 border border-slate-300 rounded text-sm text-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 uppercase mb-2">VAT Number</label>
                      <input
                        type="text"
                        value={settings.vat_number}
                        onChange={e => setSettings({ ...settings, vat_number: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded text-sm text-slate-900"
                      />
                    </div>
                  </div>

                  <div className="border-t border-slate-200 pt-4">
                    <h3 className="text-xs font-semibold text-slate-900 uppercase mb-3">Bank Details</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <label className="block text-xs font-semibold text-slate-700 uppercase mb-2">Bank Name</label>
                        <input
                          type="text"
                          value={settings.bank_name}
                          onChange={e => setSettings({ ...settings, bank_name: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded text-sm text-slate-900"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 uppercase mb-2">Sort Code</label>
                        <input
                          type="text"
                          value={settings.sort_code}
                          onChange={e => setSettings({ ...settings, sort_code: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded text-sm text-slate-900"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 uppercase mb-2">Account Number</label>
                        <input
                          type="text"
                          value={settings.account_number}
                          onChange={e => setSettings({ ...settings, account_number: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded text-sm text-slate-900"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-slate-200 pt-4">
                    <h3 className="text-xs font-semibold text-slate-900 uppercase mb-3">Company Information</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 uppercase mb-2">Company Registration</label>
                        <input
                          type="text"
                          value={settings.company_registration}
                          onChange={e => setSettings({ ...settings, company_registration: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded text-sm text-slate-900"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 uppercase mb-2">Footer Text</label>
                        <textarea
                          value={settings.invoice_footer_text}
                          onChange={e => setSettings({ ...settings, invoice_footer_text: e.target.value })}
                          rows={4}
                          className="w-full px-3 py-2 border border-slate-300 rounded text-sm text-slate-900"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-slate-200 pt-4 flex gap-2">
                    <button
                      onClick={handleSaveSettings}
                      disabled={settingsLoading}
                      className="px-4 py-2 bg-slate-900 text-white text-xs font-semibold uppercase rounded hover:bg-slate-800 disabled:opacity-50"
                    >
                      Save Settings
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </PortalLayout>
  )
}
